// UploadManager — singleton który przetwarza kolejkę zdjęć w tle.
//
// Flow:
//   start() → listenery: 'online', 'sw message', 'visibilitychange' (widoczna), 'pageshow'
//             + interwał 30 s (gdy kolejka niepusta) → processQueue()
//   processQueue() → pobierz pending → dla każdego upload POST /api/photos (1 naraz)
//     - 201 → removeFromQueue, emit 'photo-uploaded'
//     - 4xx / 5xx / timeout → retryCount++, exponential backoff + timer ponowienia
//     - 401 → stop, emit 'auth-expired' (user musi relogować)
//     - max retries → status='failed'
//     - sukces uploadu → failed z błędem SIECI wracają do pending (wariant A, 03.11.2026)
//
// V3 słaby zasięg B-1 (12.09.2026, T Martina):
//   - B3: limit 60 s per plik (AbortController) → timeout = zwykłe ponowienie z backoffem.
//   - B4: GET-y UI mają pierwszeństwo — przed każdym uploadem czekamy, aż UI nie ma GET-ów
//     w locie (netActivity). Triggery: online, visibilitychange (widoczna), pageshow,
//     interwał 30 s przy niepustej kolejce, po zakolejkowaniu (SlotButton), po nieudanym
//     uploadzie timer wg backoffu (dotąd nikt nie budził kolejki — harness 3g-loss FAZY A).
//   - B9: Tryb lokalny = kolejka NIE wysyła; wyjątek force („Wyślij teraz"/„Ponów").
//   - B6: rejestr prób wysyłki (upload_log): rozmiar przed/po downscale, czas, próby,
//     typ sieci (gdy dostępny) — widoczny w Serwisie i w diagnostyce.
//   - B12a: duplikat w tle → jak dotąd „Utracone", ale zdarzenie `photo-lost` niesie
//     współrzędne slotu + powód (czerwony znacznik w slocie + toast).
//
// Subskrypcja: subscribe(fn) zwraca unsubscriber. Emituje eventy:
//   { type: 'processing-started' }
//   { type: 'processing-done' }
//   { type: 'photo-uploaded', id, remaining, addressId, floor, subfolder, slotType }
//   { type: 'upload-failed', id, retryCount, remaining, error }
//   { type: 'queue-changed', count }
//   { type: 'photo-lost', id, remaining, reason, addressId, floor, subfolder, slotType }
//   { type: 'auth-expired' }

import {
  getPhotoQueue,
  updateQueueItem,
  removeFromQueue,
  getQueueCount,
  addToLostRegistry,
  addUploadLogEntry,
} from './offlineDB.js';
import { getToken, clearSession } from '../hooks/useAuth.js';
import { VIEWER_AK_HEADER } from '../shared/viewerAkMode.js';
import { API_BASE } from '../config.js';
import { isLocalMode } from './localMode.js';
import { UPLOAD_TIMEOUT_MS, UI_GET_TIMEOUT_MS, waitForUiIdle, readNetworkInfo } from './netActivity.js';
// UZUPEŁNIENIE 2 (12.09): znacznik ostatniego SUKCESU wysyłki (zator w banerze liczony od
// sukcesu, nie od próby; stopka „ostatnie wysłane X temu").
import { recordUploadSuccess } from './queueHealth.js';

const MAX_RETRIES = 5;
const BACKOFF_BASE_MS = 5_000; // 5s, 10s, 20s, 40s, 80s
export const QUEUE_INTERVAL_MS = 30_000; // B4 (DN3, T Martina): interwał tła 30 s
// Samoreanimacja (MONIT 2026-11-03_1655, wariant A, T Martina): ile razy jeden wpis może
// wrócić z failed do pending SAM (po sukcesie innego uploadu). Ochrona baterii przy pliku,
// który przy danym uplinku nigdy nie zdąży; dalej ratuje tylko „Erneut versuchen".
export const MAX_AUTO_REVIVES = 3;

function backoffDelay(retryCount) {
  return BACKOFF_BASE_MS * Math.pow(2, Math.max(0, retryCount - 1));
}

/**
 * Błąd SIECI = telefon nie dostał odpowiedzi serwera (`timeout 60 s`, iOS `Load failed`,
 * Chrome `Failed to fetch`). Odpowiedzi serwera markRetry zapisuje zawsze jako `HTTP nnn`
 * — te NIE są błędem sieci (realny, powtarzalny błąd; reanimacja = pętla).
 */
export function isNetworkError(lastError) {
  return typeof lastError === 'string' && lastError.length > 0 && !lastError.startsWith('HTTP ');
}

export class UploadManager {
  constructor() {
    this.processing = false;
    this.onlineListener = null;
    this.swMessageListener = null;
    this.visibilityListener = null;
    this.pageShowListener = null;
    this.intervalTimer = null;
    this.retryTimer = null;
    this.listeners = new Set();
    // Limity jako pola (testy skracają bez czekania minuty).
    this.uploadTimeoutMs = UPLOAD_TIMEOUT_MS;
    this.uiIdleWaitMs = UI_GET_TIMEOUT_MS;
  }

  /**
   * GATE'Y SUPPORT (monit 1215 zad.3): opts.sendEnabled=false (role serwisowe) =
   * ZERO toru wysyłkowego — bez listenerów online/SW i bez processQueue (bramka
   * backendu i tak odbija 403, a serie 403 nadpisywałyby diagnostyczne lastError).
   * Skan martwych blobów (R2b-b) biegnie w KAŻDYM trybie — decyzja Martina:
   * utracone wpisy znikają z licznika do rejestru „Utracone" niezależnie od roli.
   */
  start(opts = {}) {
    const sendEnabled = opts.sendEnabled !== false;
    if (typeof window === 'undefined') return;

    if (!sendEnabled) {
      // Tryb serwisowy: tylko porządki lokalne (zombie + skan), zero wysyłek.
      this.recoverZombies()
        .catch(() => {})
        .finally(() => { this.scanForLostContent().catch(() => {}); });
      return;
    }

    if (this.onlineListener) return; // already started (idempotent)

    this.onlineListener = () => this.processQueue();
    window.addEventListener('online', this.onlineListener);

    // V3 B4: powrót do aplikacji (Android ubija PWA przy aparacie, iOS usypia kartę)
    // = kolejka rusza sama, bez dotykania ekranu.
    if (typeof document !== 'undefined') {
      this.visibilityListener = () => {
        if (document.visibilityState === 'visible') this.processQueue();
      };
      document.addEventListener('visibilitychange', this.visibilityListener);
    }
    this.pageShowListener = () => this.processQueue();
    window.addEventListener('pageshow', this.pageShowListener);

    // V3 B4 (DN3): interwał 30 s w tle, tylko gdy kolejka niepusta i nic nie leci.
    this.intervalTimer = setInterval(() => {
      if (this.processing) return;
      getQueueCount().then((n) => { if (n > 0) this.processQueue(); }).catch(() => {});
    }, QUEUE_INTERVAL_MS);

    // Słuchaj SW postMessage (Background Sync / TRIGGER_SYNC broadcast → ping od SW).
    // FAZA F (2026-06-12): SW wysyła {type:'process-queue', force?:bool}. Wcześniej SW
    // broadcastował {type:'TRIGGER_SYNC'} a my słuchaliśmy 'process-queue' → mismatch,
    // SW NIGDY nie budził kolejki (martwy background-sync). Teraz typy uzgodnione
    // (sw.js v15), force z przycisku „Wyślij teraz" propaguje się też cross-tab.
    if (navigator.serviceWorker) {
      this.swMessageListener = (event) => {
        if (event.data?.type === 'process-queue') {
          this.processQueue({ force: event.data.force === true });
        }
      };
      navigator.serviceWorker.addEventListener('message', this.swMessageListener);
    }

    // FAZA F-6 (2026-06-12): jednorazowe recovery zombie przy starcie appki —
    // itemy zostawione w 'uploading' przez ubity proces (telefon zabił PWA w trakcie
    // fetch) wisiały wiecznie (processQueue je pomija). Reset → 'pending' PRZED
    // pierwszym processQueue.
    this.recoverZombies()
      .catch(() => {})
      .finally(() => {
        // R2b-b: jednorazowy skan martwych blobów przy starcie apki — PRZED
        // pierwszym processQueue (martwe wpisy nie mają młócić 400-tek).
        this.scanForLostContent()
          .catch(() => {})
          .finally(() => {
            // Jeśli już online — uruchom od razu
            if (navigator.onLine) this.processQueue();
          });
      });
  }

  /**
   * FAZA F-6: itemy 'uploading' = zombie po ubitym procesie (status ustawiany przed
   * fetch, nigdy nie cofnięty gdy proces zginął). Wykonywane raz w start().
   * @returns {Promise<number>} ile itemów odzyskano
   */
  async recoverZombies() {
    const queue = await getPhotoQueue();
    let recovered = 0;
    for (const item of queue) {
      if (item.status === 'uploading') {
        await updateQueueItem(item.id, { status: 'pending' });
        recovered++;
      }
      // HOTFIX 06.07: wpisy z bugiem InfoScreen (subfolder "gebaeude" małe g →
      // wieczne 400 invalid_subfolder, po MAX_RETRIES status='failed' pomijany
      // przez processQueue). Jednorazowa naprawa rekordu + reset licznika, żeby
      // zdjęcia Budynek/Domofon wysłały się same po aktualizacji apki.
      if (item.subfolder === 'gebaeude') {
        await updateQueueItem(item.id, {
          subfolder: 'Gebaeude', status: 'pending', retryCount: 0, lastRetryAt: null,
        });
        recovered++;
      }
    }
    if (recovered > 0) {
      const count = await getQueueCount().catch(() => 0);
      this.emit({ type: 'queue-changed', count });
    }
    return recovered;
  }

  /**
   * R2b-b: realny odczyt treści wpisu kolejki.
   * - Wpisy BAJTOWE (R2b-a, `bytes` w rekordzie): treść żyje w IDB — martwe tylko
   *   gdy puste; Blob budowany z bajtów.
   * - Wpisy LEGACY (`blob`): iOS/WebKit umie zniszczyć zawartość Bloba zostawiając
   *   metadane (size>0, treści brak — incydent D.Tyrek 17–19.08). Jedyny pewny
   *   test to faktyczny odczyt (arrayBuffer) — rzuca albo daje 0 bajtów.
   *   Blob do wysyłki budujemy z ODCZYTANYCH bajtów (fetch nie streamuje już
   *   z podejrzanego źródła).
   * @returns {Promise<{alive: boolean, blob?: Blob}>}
   */
  async probeItem(item) {
    if (item.bytes != null) {
      const len = item.bytes.byteLength ?? item.bytes.length ?? 0;
      if (!len) return { alive: false };
      return { alive: true, blob: new Blob([item.bytes], { type: item.blobType || 'image/jpeg' }) };
    }
    const src = item.blob;
    if (!src) return { alive: false };
    try {
      const buf = typeof src.arrayBuffer === 'function'
        ? await src.arrayBuffer()
        : await new Blob([src]).arrayBuffer(); // legacy raw Uint8Array
      if (!buf || buf.byteLength === 0) return { alive: false };
      return { alive: true, blob: new Blob([buf], { type: src.type || 'image/jpeg' }) };
    } catch {
      return { alive: false };
    }
  }

  /**
   * R2b-b (decyzja Martina 19.08): martwy wpis ZNIKA z aktywnej kolejki (licznik
   * „czeka" spada), a jego metadane lądują w rejestrze „Utracone" (ekran Serwis
   * + payload diagnostyki). Zero cichego znikania bez śladu.
   * V3 B12a: zdarzenie niesie współrzędne slotu + powód (znacznik w slocie, toast).
   */
  async moveToLost(item, reason = 'treść utracona przez system', extra = {}) {
    await addToLostRegistry({
      addressId: item.addressId,
      floor: item.floor,
      subfolder: item.subfolder,
      slotType: item.slotType,
      fileName: item.fileName,
      declaredSize: item.blobSize ?? item.bytes?.byteLength ?? item.blob?.size ?? null,
      createdAt: item.createdAt ?? null,
      reason,
      ...extra,
    }).catch(() => {});
    await removeFromQueue(item.id);
    const count = await getQueueCount().catch(() => 0);
    this.emit({
      type: 'photo-lost', id: item.id, remaining: count, reason,
      addressId: item.addressId, floor: item.floor, subfolder: item.subfolder, slotType: item.slotType,
      ...extra,
    });
    this.emit({ type: 'queue-changed', count });
  }

  /**
   * R2b-b: skan całej kolejki pod kątem martwych treści (start apki, każda rola).
   * Wpisy 'uploading' pomijane (zombie sprząta recoverZombies PRZED skanem).
   * @returns {Promise<number>} ile wpisów przeniesiono do rejestru „Utracone"
   */
  async scanForLostContent() {
    const queue = await getPhotoQueue();
    let lostCount = 0;
    for (const item of queue) {
      if (item.status === 'uploading') continue;
      const probe = await this.probeItem(item);
      if (!probe.alive) {
        await this.moveToLost(item);
        lostCount++;
      }
    }
    const remaining = await getQueueCount().catch(() => 0);
    // Toast w OfflineBanner: „Usunięto N zdjęć utraconych przez system…"
    this.emit({ type: 'lost-scan-done', lostCount, remaining });
    return lostCount;
  }

  stop() {
    if (this.onlineListener && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineListener);
      this.onlineListener = null;
    }
    if (this.visibilityListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
    if (this.pageShowListener && typeof window !== 'undefined') {
      window.removeEventListener('pageshow', this.pageShowListener);
      this.pageShowListener = null;
    }
    if (this.intervalTimer) { clearInterval(this.intervalTimer); this.intervalTimer = null; }
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    if (this.swMessageListener && navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', this.swMessageListener);
      this.swMessageListener = null;
    }
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  emit(event) {
    for (const fn of this.listeners) {
      try { fn(event); } catch {}
    }
  }

  async getCount() {
    return getQueueCount();
  }

  /**
   * V3 B4: po nieudanej próbie kolejka budzi się SAMA po oknie backoffu (dotąd czekała
   * na event online / ręczny przycisk). Jeden timer; kolejne wywołania skracają go
   * do najkrótszego okna.
   */
  scheduleRetry(delayMs) {
    if (typeof setTimeout !== 'function') return;
    const when = Date.now() + Math.max(250, delayMs) + 50;
    if (this.retryTimer && this.retryTimerAt && this.retryTimerAt <= when) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimerAt = when;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.retryTimerAt = null;
      this.processQueue().catch(() => {});
    }, when - Date.now());
    // Node (testy): timer nie trzyma procesu.
    if (typeof this.retryTimer?.unref === 'function') this.retryTimer.unref();
  }

  /**
   * Przetwarza kolejkę. Idempotent — jeśli już procesuje, wraca od razu.
   *
   * FAZA F-5 (2026-06-12): opts.force=true (przycisk „Wyślij teraz" / trigger()):
   *   (a) jednorazowo reanimuje failed → pending (retryCount=0; stary lastError
   *       zachowany w lastFailedError do diagnostyki),
   *   (b) ignoruje okno backoff w tym przebiegu,
   *   (c) V3 B9: omija pauzę Trybu lokalnego (jawne nadpisanie technika/admina).
   * Auto-przebiegi (online event / SW background-sync / interwał) idą BEZ force — failed-skip
   * i backoff zostają jako ochrona przed pętlą przy realnym, powtarzalnym błędzie.
   *
   * @param {Object|boolean} [opts] - { force?: boolean }
   */
  async processQueue(opts = {}) {
    const force = opts === true || opts?.force === true;
    if (this.processing) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    if (!force && isLocalMode()) return; // B9: pauza synchronizacji (wyjątek: Wyślij teraz)
    this.processing = true;
    this.emit({ type: 'processing-started' });

    let nextRetryIn = null;
    try {
      // F-5(a): reanimacja dead-endów PRZED pobraniem kolejki do przebiegu.
      if (force) {
        const all = await getPhotoQueue();
        for (const item of all) {
          if (item.status === 'failed' || (item.retryCount || 0) >= MAX_RETRIES) {
            await updateQueueItem(item.id, {
              status: 'pending',
              retryCount: 0,
              lastRetryAt: null,
              lastFailedError: item.lastError || item.lastFailedError || null,
            });
          }
        }
      }

      const queue = await getPhotoQueue();
      // Najstarsze pierwsze (kolejność zdjęć = kolejność Fot-N).
      queue.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      for (const item of queue) {
        if (item.status === 'uploading') continue; // in-flight (zombie sprząta start())
        if (!force && (item.retryCount || 0) >= MAX_RETRIES) continue;
        if (!force && item.status === 'failed') continue;
        // Respektuj backoff (F-5(b): force ignoruje okno w tym przebiegu)
        if (!force && item.lastRetryAt) {
          const wait = backoffDelay(item.retryCount || 0);
          const left = wait - (Date.now() - item.lastRetryAt);
          if (left > 0) {
            nextRetryIn = nextRetryIn == null ? left : Math.min(nextRetryIn, left);
            continue;
          }
        }
        // Sieć mogła paść / Tryb lokalny mógł się włączyć w trakcie serii.
        if (typeof navigator !== 'undefined' && !navigator.onLine) break;
        if (!force && isLocalMode()) break;
        // V3 B4: GET-y UI mają pierwszeństwo — nie startuj uploadu, gdy UI czeka na dane.
        await waitForUiIdle(this.uiIdleWaitMs);
        const outcome = await this.uploadOne(item);
        if (outcome === 'retry') {
          const wait = backoffDelay((item.retryCount || 0) + 1);
          nextRetryIn = nextRetryIn == null ? wait : Math.min(nextRetryIn, wait);
        }
        if (outcome === 'auth') break;
      }
    } finally {
      this.processing = false;
      this.emit({ type: 'processing-done' });
      const count = await getQueueCount().catch(() => 0);
      this.emit({ type: 'queue-changed', count });
      // Wariant A: zreanimowane wpisy czekały w migawce jako failed → następny przebieg od razu.
      const revived = this.revivedInPass === true;
      this.revivedInPass = false;
      if (count > 0) {
        if (revived) this.scheduleRetry(0);
        else if (nextRetryIn != null) this.scheduleRetry(nextRetryIn);
      }
    }
  }

  /**
   * Wysyłka jednego wpisu. Zwraca: 'ok' | 'retry' | 'lost' | 'auth' | 'skip'.
   */
  async uploadOne(item) {
    await updateQueueItem(item.id, { status: 'uploading' });
    this.emit({ type: 'queue-changed', count: await getQueueCount() });

    const form = new FormData();
    form.append('addressId', item.addressId);
    form.append('floor', item.floor);
    // HOTFIX 06.07: wpisy zakolejkowane z bugiem InfoScreen (sloty Budynek/Domofon)
    // mają subfolder "gebaeude" (małe g) → backend odrzuca 400 invalid_subfolder
    // na zawsze. Samonaprawa starych rekordów IDB przy wysyłce: poprawna wartość
    // to "Gebaeude" (VALID_SUBFOLDER_RE).
    form.append('subfolder', item.subfolder === 'gebaeude' ? 'Gebaeude' : item.subfolder);
    form.append('slotType', item.slotType);
    // R2b-b (zastępuje hotfix 0-B z 06.07): realny odczyt treści przy KAŻDEJ próbie
    // wysyłki — martwy blob (iOS zniszczył zawartość; metadane kłamią size>0) i pusty
    // 0-B lądują w rejestrze „Utracone" zamiast młócić 400-tki albo znikać po cichu.
    const probe = await this.probeItem(item);
    if (!probe.alive) {
      await this.moveToLost(item);
      await this.logAttempt(item, { bytes: 0, durationMs: 0, result: 'lost' });
      return 'lost';
    }
    form.append('file', probe.blob, item.fileName || 'photo.jpg');
    const sentBytes = probe.blob.size;

    const token = getToken();
    // V3 B3 (DN1): limit 60 s per plik → po nim zwykłe ponowienie z backoffem.
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timedOut = false;
    const timer = controller ? setTimeout(() => { timedOut = true; controller.abort(); }, this.uploadTimeoutMs) : null;
    if (timer && typeof timer.unref === 'function') timer.unref();
    const t0 = Date.now();
    let res;
    try {
      res = await fetch(`${API_BASE}/api/photos/`, {
        method: 'POST',
        // E7a: wpis małego admina z trybu AK = nagłówek trybu (serwer zapisze pod teamem AK; dla innej roli ignorowany).
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(item.viewerAk ? { [VIEWER_AK_HEADER]: 'ak' } : {}),
        },
        body: form,
        signal: controller ? controller.signal : undefined,
      });
    } catch (err) {
      const reason = timedOut ? `timeout ${Math.round(this.uploadTimeoutMs / 1000)} s` : (err.message || 'network_error');
      await this.logAttempt(item, { bytes: sentBytes, durationMs: Date.now() - t0, result: timedOut ? 'timeout' : 'network', error: reason });
      await this.markRetry(item, reason);
      return 'retry';
    } finally {
      if (timer) clearTimeout(timer);
    }
    const durationMs = Date.now() - t0;

    if (res.status === 401) {
      // Token expired — zatrzymaj wszystko, user musi zalogować
      await updateQueueItem(item.id, { status: 'pending' });
      await this.logAttempt(item, { bytes: sentBytes, durationMs, result: 'http_401' });
      clearSession();
      this.emit({ type: 'auth-expired' });
      this.stop();
      return 'auth';
    }

    if (res.ok) {
      // 201 = nowy upload; 200 + duplicate:true = serwer już ma to zdjęcie
      // (FAZA F-8, idempotencja 23505) — oba czyszczą kolejkę.
      await removeFromQueue(item.id);
      await this.logAttempt(item, { bytes: sentBytes, durationMs, result: res.status === 200 ? 'duplicate_same_slot' : 'ok' });
      // UZUPEŁNIENIE 2: sukces dostarczenia (201 lub 200 „już mam") przesuwa zegar zatoru.
      await recordUploadSuccess(Date.now()).catch(() => {});
      // Wariant A (03.11): sukces = dowód, że sieć działa → failed z błędem sieci wracają do kolejki.
      await this.reviveNetworkFailed().catch(() => {});
      const count = await getQueueCount();
      this.emit({
        type: 'photo-uploaded', id: item.id, remaining: count,
        addressId: item.addressId, floor: item.floor, subfolder: item.subfolder, slotType: item.slotType,
      });
      this.emit({ type: 'queue-changed', count });
      return 'ok';
    }

    // Guard anty-miniatura (monit 1155, 28.08): 400 image_too_small to błąd TRWAŁY
    // (plik jest pomniejszoną kopią z galerii — żaden retry tego nie naprawi; lekcja:
    // wieczne 400 invalid_subfolder, hotfix 06.07). Wpis przez moveToLost → znika
    // z licznika „czeka" i ląduje w rejestrze „Utracone" (ekran Serwis) — zero
    // cichego znikania. Inne 400 idą normalną ścieżką retry.
    if (res.status === 400) {
      let errCode = null;
      try { errCode = (await res.json())?.error || null; } catch { /* body nie-JSON */ }
      if (errCode === 'image_too_small') {
        await this.moveToLost(item, 'odrzucone: za niska rozdzielczość');
        await this.logAttempt(item, { bytes: sentBytes, durationMs, result: 'rejected_too_small' });
        return 'lost';
      }
    }

    // PAKIET 5 (monit 1700, 30.08): 409 duplicate_photo to błąd TRWAŁY (ten sam
    // plik leży już w innym slocie/adresie — doktryna: twarda blokada duplikatów
    // w całej bazie; retry tego nie naprawi). Wzorzec image_too_small: moveToLost
    // → rejestr „Utracone" z lokalizacją istniejącego zdjęcia, zero pętli retry.
    // (Retry TEGO SAMEGO zdjęcia do TEGO SAMEGO slotu nie trafia tutaj — backend
    // zwraca wtedy 200 + duplicate:true = idempotencja, kolejka czyści się wyżej.)
    // V3 B12a (minimum B-1): zdarzenie niesie lokalizację → znacznik w slocie + toast.
    if (res.status === 409) {
      let errBody = null;
      try { errBody = await res.json(); } catch { /* body nie-JSON */ }
      if (errBody?.error === 'duplicate_photo') {
        const loc = errBody.location || {};
        const gdzie = [loc.addressLabel, loc.floor, loc.subfolder].filter(Boolean).join(', ');
        await this.moveToLost(item, `odrzucone: duplikat — zdjęcie już jest w: ${gdzie || 'innym slocie'}`, {
          duplicate: true, location: loc,
        });
        await this.logAttempt(item, { bytes: sentBytes, durationMs, result: 'rejected_duplicate' });
        return 'lost';
      }
    }

    // GATE'Y SUPPORT (monit 1215 zad.3): 403 z bramki rolowej to NIE błąd wpisu —
    // próba się nie liczy: bez retryCount++, bez dotykania lastError (materiał
    // diagnostyczny — 19.08 serie 403 nadpisałyby prawdziwe HTTP 400). Wpis wraca
    // do pending. Inne 403 (niespodziewane) idą normalną ścieżką retry.
    if (res.status === 403) {
      let errCode = null;
      try { errCode = (await res.json())?.error || null; } catch { /* body nie-JSON */ }
      if (errCode === 'forbidden_support_role') {
        await updateQueueItem(item.id, { status: 'pending' });
        const count = await getQueueCount().catch(() => 0);
        this.emit({ type: 'queue-changed', count });
        return 'skip';
      }
    }

    // 4xx / 5xx — retry z backoff
    await this.logAttempt(item, { bytes: sentBytes, durationMs, result: `http_${res.status}` });
    await this.markRetry(item, `HTTP ${res.status}`);
    return 'retry';
  }

  /**
   * Wariant A (MONIT 2026-11-03_1655, casus Kollostr. 8a / Juri #3): przy słabym zasięgu
   * 5 prób wypala się w ~3 min (backoff 5..80 s) i wpis zostaje failed na zawsze, aż technik
   * naciśnie „Erneut versuchen". Po udanym uploadzie wpisy failed z błędem SIECI wracają do
   * pending z retryCount=0 (jak F-5(a), stary błąd w lastFailedError), max MAX_AUTO_REVIVES
   * razy na wpis. Failed z odpowiedzią serwera (HTTP nnn) zostają nietknięte.
   * Wpisy z migawki bieżącego przebiegu mają jeszcze status failed → processQueue planuje
   * od razu następny przebieg (revivedInPass).
   * @returns {Promise<number>} ile wpisów zreanimowano
   */
  async reviveNetworkFailed() {
    const queue = await getPhotoQueue();
    let revived = 0;
    for (const item of queue) {
      if (item.status !== 'failed') continue;
      if (!isNetworkError(item.lastError)) continue;
      const autoRevives = item.autoRevives || 0;
      if (autoRevives >= MAX_AUTO_REVIVES) continue;
      await updateQueueItem(item.id, {
        status: 'pending',
        retryCount: 0,
        lastRetryAt: null,
        lastFailedError: item.lastError,
        autoRevives: autoRevives + 1,
      });
      revived++;
    }
    if (revived > 0) {
      this.revivedInPass = true;
      this.emit({ type: 'queue-changed', count: await getQueueCount().catch(() => 0) });
    }
    return revived;
  }

  /** V3 B6: wpis rejestru prób wysyłki (best-effort, zero binariów). */
  async logAttempt(item, { bytes, durationMs, result, error = null }) {
    try {
      const net = readNetworkInfo();
      await addUploadLogEntry({
        queueId: item.id,
        addressId: item.addressId ?? null,
        floor: item.floor ?? null,
        subfolder: item.subfolder ?? null,
        slotType: item.slotType ?? null,
        fileName: item.fileName ?? null,
        bytes: bytes ?? null,
        originalBytes: item.meta?.originalBytes ?? null,
        downscaled: item.meta?.downscaled ?? null,
        width: item.meta?.width ?? null,
        height: item.meta?.height ?? null,
        attempt: (item.retryCount || 0) + 1,
        durationMs,
        result,
        error,
        online: typeof navigator !== 'undefined' ? navigator.onLine !== false : null,
        netType: net.effectiveType,
        downlink: net.downlink,
        rtt: net.rtt,
      });
    } catch { /* rejestr nie może zatrzymać wysyłki */ }
  }

  async markRetry(item, error) {
    const newRetry = (item.retryCount || 0) + 1;
    const reachedMax = newRetry >= MAX_RETRIES;
    await updateQueueItem(item.id, {
      status: reachedMax ? 'failed' : 'pending',
      retryCount: newRetry,
      lastRetryAt: Date.now(),
      lastError: error,
    });
    const count = await getQueueCount();
    this.emit({ type: 'upload-failed', id: item.id, retryCount: newRetry, remaining: count, error });
    this.emit({ type: 'queue-changed', count });
  }

  /** Test helper — force trigger bez eventów. */
  async forceProcess() {
    return this.processQueue();
  }

  // S3 (Sprint Hygiene & Polish 2026-05-08): API alignment z offline/uploadManager
  // (orphan removed). ForceUploadButton.jsx wywołuje .trigger() + .requestBackgroundSync()
  // — tu są aliasy do existing processQueue + SW sync register, żeby zachować
  // Sprint #2 ForceUploadButton API bez zmian po orphan cleanup.

  /**
   * Sprint #2 + FAZA F-5 (2026-06-12): manual trigger = PRAWDZIWY force.
   * Reanimuje failed→pending (retryCount=0, lastError→lastFailedError) i ignoruje
   * backoff w tym przebiegu. Wcześniej alias zwykłego processQueue → „Wyślij teraz"
   * nie robił NIC dla itemów failed/w-backoffie (kolejka 15 u [D-F], 12.06).
   */
  trigger() {
    return this.processQueue({ force: true });
  }

  /**
   * KROK 1 (18.08): ponowienie POJEDYNCZEGO wpisu (ekran Serwis, rola
   * support-plus). Reset licznika prób + upload TYLKO tego wpisu — bez
   * ruszania reszty kolejki. Respektuje processing-lock (jedna wysyłka
   * naraz — ochrona przed wyścigiem photo_index MAX+1).
   * @returns {Promise<{ok: boolean, reason?: string}>}
   */
  async retryOne(id) {
    if (this.processing) return { ok: false, reason: 'busy' };
    if (typeof navigator !== 'undefined' && !navigator.onLine) return { ok: false, reason: 'offline' };
    const queue = await getPhotoQueue();
    const item = queue.find((it) => it.id === id);
    if (!item) return { ok: false, reason: 'not_found' };
    this.processing = true;
    this.emit({ type: 'processing-started' });
    try {
      const fresh = await updateQueueItem(id, {
        status: 'pending', retryCount: 0, lastRetryAt: null,
        lastFailedError: item.lastError || item.lastFailedError || null,
      });
      await this.uploadOne(fresh || item);
      return { ok: true };
    } finally {
      this.processing = false;
      this.emit({ type: 'processing-done' });
      const count = await getQueueCount().catch(() => 0);
      this.emit({ type: 'queue-changed', count });
    }
  }

  /** Sprint #2: Background Sync register dla Chrome/Android (no-op na iOS). */
  async requestBackgroundSync() {
    try {
      if (!('serviceWorker' in navigator)) return;
      const reg = await navigator.serviceWorker.ready;
      if (reg.sync) await reg.sync.register('photo-sync');
    } catch {
      // silently ignore — Background Sync nie wszędzie wspierane
    }
  }
}

export const uploadManager = new UploadManager();
